import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import { type FC } from 'react';

import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { InternalLink } from '@/components/InternalLink';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  isOwner: boolean;
  hasCosigners: boolean;
  newOrderOnClick?: () => void;
}

export const DiscretionaryOrderCompleted: FC<Props> = ({
  isOwner,
  hasCosigners,
  newOrderOnClick,
}) => {
  const texts = useTexts();
  const organizationCmId = useOrganizationCmId();
  const getTrackId = useGetTrackId();

  const alertText =
    isOwner && hasCosigners
      ? texts.orders.form.orderReceived.sentToCosigner
      : texts.orders.form.orderReceived.received;

  return (
    <WithGap gap="48">
      <h2 data-test="completed-step-header">
        {texts.orders.form.steps.finished.title}
      </h2>

      <Alert title={alertText} variant="success">
        <>
          {texts.orders.form.orderReceived.message}
          {/* Have a space between the message and the link text. */}{' '}
          <InternalLink
            to={`/${organizationCmId}/trading/orders`}
            data-trackid={getTrackId('go-to-order-list', TrackingElement.Link)}
          >
            {texts.orders.form.orderReceived.linkText}
          </InternalLink>
        </>
      </Alert>

      <WithGap gap="8" direction="row">
        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/discretionary/deposit`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-deposit-order"
          data-trackid={getTrackId('go-to-deposit', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.deposit}
        </Button>

        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/discretionary/withdrawal`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-withdrawal-order"
          data-trackid={getTrackId('go-to-withdrawal', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.withdrawal}
        </Button>

        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/discretionary/transfer`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-transfer-order"
          data-trackid={getTrackId('go-to-transfer', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.transfer}
        </Button>

        <Button
          variant="tertiary"
          iconRight="arrow_right"
          type="button"
          to={`/${organizationCmId}`}
          as={InternalLink}
          data-trackid={getTrackId('go-to-overview', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.overview}
        </Button>
      </WithGap>
    </WithGap>
  );
};
